import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocale, formatNumber as formatNumberLocale } from "@/i18n/format";
import { useBrandContext } from "@/contexts/brand-context";
import {
  TimeRangeSelector,
  type AnalyticsRange,
} from "@/components/analytics/time-range-selector";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { Instagram, AlertCircle, TrendingUp, TrendingDown } from "lucide-react";
import { cn } from "@/lib/utils";

const BASE = "/api";

interface InstagramOverview {
  account: {
    id: string;
    username: string | null;
    lastSyncedAt: string | null;
    lastSyncError: string | null;
  };
  range: { from: string; to: string; days: number };
  totals: Record<string, string>;
  prevTotals: Record<string, string>;
  reachSeries: Array<{ date: string; value: string }>;
}

interface InstagramBreakdownRow {
  metric: string;
  bucket: string;
  value: string;
}

interface InstagramDemographicsRow {
  bucket: string;
  value: string;
  percent: number;
}

interface StoryInsightRow {
  storyId: string;
  postedAt: string | null;
  mediaType: string | null;
  thumbnailUrl: string | null;
  permalink: string | null;
  source: string;
  views: string;
  reach: string;
  replies: string;
  shares: string;
  navigation: string;
  tapsForward: string;
  tapsBack: string;
  exits: string;
  swipesForward: string;
  profileVisits: string;
  /** Null = Instagram didn't report follows for this story. */
  follows: string | null;
  totalInteractions: string;
}

async function fetchJson<T>(url: string): Promise<T | null> {
  const r = await fetch(url, { credentials: "include" });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`Request failed: ${url}`);
  return r.json() as Promise<T>;
}

function useIgOverview(brandId: string, days: AnalyticsRange) {
  return useQuery({
    queryKey: ["ig-overview", brandId, days],
    queryFn: () =>
      fetchJson<InstagramOverview>(
        `${BASE}/brands/${brandId}/analytics/instagram/overview?days=${days}`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

function useIgBreakdown(brandId: string, dimension: string) {
  return useQuery({
    queryKey: ["ig-breakdown", brandId, dimension],
    queryFn: () =>
      fetchJson<InstagramBreakdownRow[]>(
        `${BASE}/brands/${brandId}/analytics/instagram/breakdowns/${dimension}`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

function useIgDemographics(brandId: string, dimension: string) {
  return useQuery({
    queryKey: ["ig-demographics", brandId, dimension],
    queryFn: () =>
      fetchJson<InstagramDemographicsRow[]>(
        `${BASE}/brands/${brandId}/analytics/instagram/demographics?dimension=${dimension}`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

function useIgStories(brandId: string, days: AnalyticsRange) {
  return useQuery({
    queryKey: ["ig-stories", brandId, days],
    queryFn: () =>
      fetchJson<{ stories: StoryInsightRow[] }>(
        `${BASE}/brands/${brandId}/analytics/instagram/stories?days=${days}`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

function formatIgNumber(n: string | number | null | undefined, locale: string): string {
  if (n == null || n === "") return "—";
  const num = typeof n === "string" ? Number(n) : n;
  if (!Number.isFinite(num)) return "—";
  return formatNumberLocale(num, locale);
}

function computeDeltaPct(current: string | undefined, previous: string | undefined): number | null {
  const c = Number(current ?? 0);
  const p = Number(previous ?? 0);
  if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0) return null;
  return ((c - p) / p) * 100;
}

function KpiCard({
  label,
  value,
  deltaPct,
}: {
  label: string;
  value: string;
  deltaPct: number | null;
}) {
  const { t } = useTranslation("analytics");
  const positive = deltaPct !== null && deltaPct > 0;
  const negative = deltaPct !== null && deltaPct < 0;
  const Icon = positive ? TrendingUp : negative ? TrendingDown : null;
  return (
    <Card className="p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      {deltaPct !== null && (
        <div
          className={cn(
            "mt-1 text-xs flex items-center gap-1",
            positive && "text-emerald-600",
            negative && "text-rose-600",
            !positive && !negative && "text-muted-foreground",
          )}
        >
          {Icon && <Icon size={12} />}
          {deltaPct > 0 ? "+" : ""}
          {deltaPct.toFixed(1)}% {t("instagram.vsPrior")}
        </div>
      )}
    </Card>
  );
}

function ReachSparkline({ series }: { series: InstagramOverview["reachSeries"] }) {
  const { t } = useTranslation("analytics");
  const locale = useLocale();
  if (series.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-6 text-center">
        {t("instagram.noReachData")}
      </div>
    );
  }
  const max = Math.max(...series.map((p) => Number(p.value)), 1);
  return (
    <div className="flex items-end gap-1 h-40 w-full">
      {series.map((p) => {
        const v = Number(p.value);
        const pct = (v / max) * 100;
        return (
          <div
            key={p.date}
            className="flex-1 bg-primary/70 hover:bg-primary rounded-t"
            style={{ height: `${pct}%` }}
            title={`${p.date}: ${formatIgNumber(v, locale)}`}
          />
        );
      })}
    </div>
  );
}

function HorizontalBarList({
  rows,
  emptyHint,
}: {
  rows: Array<{ bucket: string; value: number }>;
  emptyHint: string;
}) {
  const locale = useLocale();
  if (rows.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-6 text-center">
        {emptyHint}
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const pct = (r.value / max) * 100;
        return (
          <div key={r.bucket} className="flex items-center gap-2 text-sm">
            <div className="w-24 shrink-0 truncate" title={r.bucket}>
              {r.bucket}
            </div>
            <div className="flex-1 bg-muted rounded h-4 relative">
              <div
                className="bg-primary h-full rounded"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="w-16 text-right tabular-nums text-muted-foreground">
              {formatIgNumber(r.value, locale)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BreakdownPanel({
  title,
  brandId,
  dimension,
  metricFilter,
}: {
  title: string;
  brandId: string;
  dimension: string;
  metricFilter?: string;
}) {
  const { t } = useTranslation("analytics");
  const { data, isLoading } = useIgBreakdown(brandId, dimension);
  if (isLoading) return <Skeleton className="h-48 rounded-lg" />;
  const rows = (data ?? [])
    .filter((r) => (metricFilter ? r.metric === metricFilter : true))
    .map((r) => ({ bucket: r.bucket, value: Number(r.value) }));
  return (
    <Card className="p-4 space-y-3">
      <div className="text-sm font-medium">{title}</div>
      <HorizontalBarList rows={rows} emptyHint={t("instagram.notEnoughData")} />
    </Card>
  );
}

function DemographicsPanel({
  title,
  brandId,
  dimension,
  personalAccount,
}: {
  title: string;
  brandId: string;
  dimension: string;
  personalAccount: boolean;
}) {
  const { t } = useTranslation("analytics");
  const { data, isLoading } = useIgDemographics(brandId, dimension);
  if (isLoading) return <Skeleton className="h-48 rounded-lg" />;
  const rows = (data ?? []).map((r) => ({
    bucket: r.bucket,
    value: Number(r.value),
  }));
  const emptyHint = personalAccount
    ? t("instagram.demographics.switchToCreator")
    : t("instagram.demographics.requiresBusiness");
  return (
    <Card className="p-4 space-y-3">
      <div className="text-sm font-medium">{title}</div>
      <HorizontalBarList rows={rows} emptyHint={emptyHint} />
    </Card>
  );
}

function StoryNavigationFunnel({ row }: { row: StoryInsightRow }) {
  const { t } = useTranslation("analytics");
  const nav = Number(row.navigation);
  const tapsForward = Number(row.tapsForward);
  const exits = Number(row.exits);
  const swipesForward = Number(row.swipesForward);
  if (!Number.isFinite(nav) || nav <= 0) {
    return <div className="w-full h-2 rounded bg-muted" />;
  }
  const segments = [
    { key: "tapsForward", value: tapsForward, className: "bg-emerald-500", label: t("igStories.tapsForward") },
    { key: "exits", value: exits, className: "bg-rose-500", label: t("igStories.exits") },
    { key: "swipesForward", value: swipesForward, className: "bg-amber-500", label: t("igStories.swipesForward") },
  ];
  return (
    <div className="flex w-full h-2 rounded overflow-hidden bg-muted" title={t("igStories.funnel")}>
      {segments.map((seg) => {
        const pct = Math.max(0, Math.min(100, (seg.value / nav) * 100));
        if (pct <= 0) return null;
        return (
          <div
            key={seg.key}
            className={cn("h-full", seg.className)}
            style={{ width: `${pct}%` }}
            title={`${seg.label}: ${seg.value}`}
          />
        );
      })}
    </div>
  );
}

function StoriesSection({ brandId, days }: { brandId: string; days: AnalyticsRange }) {
  const { t } = useTranslation("analytics");
  const locale = useLocale();
  const { data, isLoading } = useIgStories(brandId, days);

  if (isLoading) return <Skeleton className="h-64 rounded-lg" />;

  const stories = data?.stories ?? [];

  return (
    <Card className="p-4 space-y-3">
      <div>
        <div className="text-sm font-medium">{t("igStories.title")}</div>
        <p className="text-xs text-muted-foreground">
          {t("igStories.subtitle", { days })}
        </p>
      </div>
      {stories.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">
          {t("igStories.empty")}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground uppercase tracking-wide text-left">
                <th className="py-2 pr-3 font-normal"> </th>
                <th className="py-2 pr-3 font-normal">{t("igStories.views")}</th>
                <th className="py-2 pr-3 font-normal">{t("igStories.reach")}</th>
                <th className="py-2 pr-3 font-normal">{t("igStories.replies")}</th>
                <th className="py-2 pr-3 font-normal">{t("igStories.follows")}</th>
                <th className="py-2 pr-3 font-normal">{t("igStories.funnel")}</th>
              </tr>
            </thead>
            <tbody>
              {stories.map((row) => (
                <tr key={row.storyId} className="border-t border-border">
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      {row.thumbnailUrl ? (
                        <img
                          src={row.thumbnailUrl}
                          alt=""
                          className="w-8 h-8 rounded object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded bg-muted shrink-0" />
                      )}
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {row.postedAt
                          ? new Date(row.postedAt).toLocaleDateString(locale)
                          : "—"}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 pr-3 tabular-nums">{formatIgNumber(row.views, locale)}</td>
                  <td className="py-2 pr-3 tabular-nums">{formatIgNumber(row.reach, locale)}</td>
                  <td className="py-2 pr-3 tabular-nums">{formatIgNumber(row.replies, locale)}</td>
                  <td className="py-2 pr-3 tabular-nums">{formatIgNumber(row.follows, locale)}</td>
                  <td className="py-2 pr-3 min-w-[120px]">
                    <StoryNavigationFunnel row={row} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

const DAYS_VALUES: AnalyticsRange[] = [7, 30, 90];
function parseDays(raw: string | null): AnalyticsRange {
  const n = Number(raw);
  return (DAYS_VALUES as number[]).includes(n) ? (n as AnalyticsRange) : 30;
}

export function BrandAnalyticsInstagramPage() {
  const { t: tl } = useTranslation("analytics");
  const locale = useLocale();
  const { brand } = useBrandContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const days = parseDays(searchParams.get("days"));
  const setDays = useCallback(
    (v: AnalyticsRange) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v === 30) next.delete("days");
          else next.set("days", String(v));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const { data, isLoading, isError } = useIgOverview(brand.id, days);

  if (isLoading) {
    return (
      <div className="px-6 py-8 max-w-7xl mx-auto w-full space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-6 py-8 max-w-7xl mx-auto w-full">
        <EmptyState
          icon={AlertCircle}
          title={tl("instagram.loadError")}
          description={tl("instagram.tryRefresh")}
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="px-6 py-8 max-w-7xl mx-auto w-full">
        <EmptyState
          icon={Instagram}
          title={tl("instagram.noAccount")}
          description={tl("instagram.noAccountDesc")}
        />
      </div>
    );
  }

  const totals = data.totals;
  const prev = data.prevTotals;
  const stale =
    data.account.lastSyncedAt &&
    Date.now() - new Date(data.account.lastSyncedAt).getTime() >
      12 * 60 * 60 * 1000;
  const personalAccount = data.account.lastSyncError === "ig_personal_account";

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto w-full space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">{tl("instagram.title")}</h1>
          {data.account.username && (
            <p className="text-sm text-muted-foreground mt-0.5">
              @{data.account.username}
            </p>
          )}
        </div>
        <TimeRangeSelector value={days} onChange={setDays} />
      </div>

      {stale && (
        <Card className="p-3 text-sm bg-amber-50 border-amber-200 text-amber-900">
          {tl("instagram.stale")}
        </Card>
      )}
      {personalAccount ? (
        <Card className="p-4 text-sm bg-amber-50 border-amber-200 text-amber-900 space-y-1">
          <div className="font-medium">{tl("instagram.switchTitle")}</div>
          <p>{tl("instagram.switchDesc")}</p>
        </Card>
      ) : (
        data.account.lastSyncError && (
          <Card className="p-3 text-sm bg-rose-50 border-rose-200 text-rose-900">
            {tl("instagram.lastSyncError")}: <code>{data.account.lastSyncError}</code>
          </Card>
        )
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label={tl("instagram.kpi.reach")}
          value={formatIgNumber(totals.reach ?? 0, locale)}
          deltaPct={computeDeltaPct(totals.reach, prev.reach)}
        />
        <KpiCard
          label={tl("instagram.kpi.views")}
          value={formatIgNumber(totals.views ?? 0, locale)}
          deltaPct={computeDeltaPct(totals.views, prev.views)}
        />
        <KpiCard
          label={tl("instagram.kpi.accountsEngaged")}
          value={formatIgNumber(totals.accounts_engaged ?? 0, locale)}
          deltaPct={computeDeltaPct(totals.accounts_engaged, prev.accounts_engaged)}
        />
        <KpiCard
          label={tl("instagram.kpi.totalInteractions")}
          value={formatIgNumber(totals.total_interactions ?? 0, locale)}
          deltaPct={computeDeltaPct(totals.total_interactions, prev.total_interactions)}
        />
      </div>

      <Card className="p-4 space-y-3">
        <div className="text-sm font-medium">{tl("instagram.reachOverTime")}</div>
        <ReachSparkline series={data.reachSeries} />
      </Card>

      <StoriesSection brandId={brand.id} days={days} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BreakdownPanel
          title={tl("instagram.breakdown.reachByMediaType")}
          brandId={brand.id}
          dimension="media_product_type"
          metricFilter="reach"
        />
        <BreakdownPanel
          title={tl("instagram.breakdown.followersVsNon")}
          brandId={brand.id}
          dimension="follow_type"
          metricFilter="reach"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DemographicsPanel
          title={tl("instagram.demographics.age")}
          brandId={brand.id}
          dimension="age"
          personalAccount={personalAccount}
        />
        <DemographicsPanel
          title={tl("instagram.demographics.gender")}
          brandId={brand.id}
          dimension="gender"
          personalAccount={personalAccount}
        />
        <DemographicsPanel
          title={tl("instagram.demographics.topCountries")}
          brandId={brand.id}
          dimension="country"
          personalAccount={personalAccount}
        />
        <DemographicsPanel
          title={tl("instagram.demographics.topCities")}
          brandId={brand.id}
          dimension="city"
          personalAccount={personalAccount}
        />
      </div>
    </div>
  );
}
